/**
 * The company a single-record page belongs to, published upward to the app shell.
 *
 * A detail page knows which legal entity its record belongs to; the app bar is
 * where the user looks to answer "which company am I in?". This is the channel
 * between them. On a collection surface nothing is published and the app bar
 * falls back to the global scope filter, so the SAME slot answers the question
 * on every page — a filter on a list, the record's own identity on a record.
 *
 * Deliberately NOT folded into `PageContext`, which has an identical publish /
 * auto-clear lifecycle. That one carries an entity breadcrumb hierarchy for the
 * AI assistant, keyed on a JSON serialization of the whole payload, and is
 * adopted by 18 pages. Company identity is a different consumer (app-bar
 * chrome), a different population (every company-scoped record page), and would
 * churn that serialization key on an unrelated axis. Same idiom, separate
 * channel.
 *
 * Contract: `docs/platform/multi-company-design.md` § "On-record company".
 */
import {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import type { ReactNode } from 'react';

export interface PageCompany {
  /**
   * The record's owning company (`companies.id`), or `null` when the record
   * genuinely has none — an S? entity such as a draft shipment or a deal before
   * it is Won. `null` is a real state that renders "unassigned", NOT the same as
   * the page having published nothing.
   */
  companyId: string | null;
  /**
   * Canonical module id (`accounting`, `sales`, …). The control hides itself
   * unless this module participates in company scoping for the tenant.
   */
  moduleId: string;
  /** The record is still loading, so `companyId` is not yet meaningful. */
  isLoading?: boolean;
  /**
   * Whether the record's company may still be changed — the page's own
   * draft/posted predicate. An S document locks its company once posted.
   * Requires {@link onChange}.
   */
  editable?: boolean;
  /**
   * Persist a company change. The PAGE owns the write and every side effect it
   * carries (re-issuing a per-company document number, re-resolving FX) — this
   * channel only surfaces the control.
   */
  onChange?: (companyId: string) => void;
}

interface PageCompanyContextValue {
  pageCompany: PageCompany | null;
  setPageCompany: (value: PageCompany | null) => void;
}

const PageCompanyCtx = createContext<PageCompanyContextValue | undefined>(undefined);

export function PageCompanyProvider({ children }: { children: ReactNode }) {
  const [pageCompany, setPageCompany] = useState<PageCompany | null>(null);
  const value = useMemo(() => ({ pageCompany, setPageCompany }), [pageCompany]);
  return <PageCompanyCtx.Provider value={value}>{children}</PageCompanyCtx.Provider>;
}

/**
 * Read the current page's company, or `null` on a collection surface.
 *
 * Returns `null` rather than throwing outside a provider: this is read by app
 * shell chrome that also renders in the external portal and in tests, where no
 * provider is mounted and "no page company" is the correct answer.
 */
export function usePageCompany(): PageCompany | null {
  return useContext(PageCompanyCtx)?.pageCompany ?? null;
}

/**
 * Publish this record's company to the app bar. Clears automatically on unmount.
 *
 * Call it UNCONDITIONALLY and from first render, passing `isLoading` while the
 * record is in flight — not after the record resolves. Publishing late leaves
 * the app-bar slot empty and then reflows the whole header cluster when the
 * fetch lands.
 *
 * @example
 * // BillDetailPage
 * useSetPageCompany({
 *   companyId: bill?.company_id ?? null,
 *   moduleId: 'accounting',
 *   isLoading,
 * });
 */
export function useSetPageCompany(value: PageCompany | null): void {
  const ctx = useContext(PageCompanyCtx);
  const setPageCompany = ctx?.setPageCompany;

  // `onChange` is typically an inline closure, so the value identity changes
  // every render. Compare the serializable fields and keep the latest callback
  // in a ref, or this effect loops.
  const onChangeRef = useRef(value?.onChange);
  onChangeRef.current = value?.onChange;

  const key = value
    ? `${value.companyId ?? ''}|${value.moduleId}|${value.isLoading ? 1 : 0}|${
        value.editable && value.onChange ? 1 : 0
      }`
    : '';

  const stableOnChange = useCallback((companyId: string) => {
    onChangeRef.current?.(companyId);
  }, []);

  const companyId = value?.companyId ?? null;
  const moduleId = value?.moduleId;
  const isLoading = value?.isLoading;
  const editable = !!value?.editable && !!value?.onChange;

  useEffect(() => {
    if (!setPageCompany) return;

    if (!moduleId) {
      setPageCompany(null);
      return;
    }

    setPageCompany({
      companyId,
      moduleId,
      isLoading,
      editable,
      onChange: editable ? stableOnChange : undefined,
    });

    return () => setPageCompany(null);
    // `key` collapses the fields above into one comparable identity; the
    // individual reads are what the body uses.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on `key`
  }, [key, setPageCompany, stableOnChange]);
}
